/**
 * Toki API Service
 * Toki app-тай холбогдох API хүсэлтүүд
 */

import { API_BASE_URL } from '../api/config';

// Environment variables
const TOKI_API_URL = process.env.REACT_APP_TOKI_API_URL || 'https://ms-api.toki.mn';
const TOKI_PUBLIC_URL = process.env.REACT_APP_TOKI_PUBLIC_URL || 'https://ms-public.toki.mn';
const TOKI_API_KEY = process.env.REACT_APP_TOKI_API_KEY || '';
const BACKEND_URL = API_BASE_URL;

/**
 * Authenticate Toki user and get JWT token
 * Same clean pattern as authenticateHambiUser — no complex response wrapping.
 *
 * @param {string} token - Toki token from URL
 * @returns {Promise<Object>} - { success, token, refreshToken, candidateId, data }
 */
export const authenticateTokiUser = async (token) => {
    try {
        const normalizedToken = typeof token === 'string' ? token.trim() : token;
        const headers = { 'Content-Type': 'application/json' };
        if (normalizedToken) {
            headers['x-user-token'] = normalizedToken;
            headers['x_user_token'] = normalizedToken;
        }
        const url = normalizedToken
            ? `${BACKEND_URL}/v1/toki/auth?token=${encodeURIComponent(normalizedToken)}`
            : `${BACKEND_URL}/v1/toki/auth`;
        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({ token: normalizedToken })
        });

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                error: data?.error || data?.message || `HTTP Error: ${response.status}`
            };
        }

        if (data.success === false) {
            return {
                success: false,
                error: data.error || data.message || 'Failed to authenticate'
            };
        }

        return {
            success: true,
            token: data.token,
            refreshToken: data.refreshToken,
            candidateId: data.candidateId,
            data: data.data || data
        };
    } catch (error) {
        console.error('Toki authenticate error:', error);
        return {
            success: false,
            error: error.message || 'Authentication error'
        };
    }
};

/**
 * Profile зургийн full URL авах
 * 
 * @param {string} profilePicURL - Profile зургийн filename
 * @returns {string} - Full URL
 */
export const getProfilePictureUrl = (profilePicURL) => {
    if (!profilePicURL) return null;

    // Хэрэв аль хэдийн full URL бол шууд буцаана
    if (profilePicURL.startsWith('http')) {
        return profilePicURL;
    }

    return `${TOKI_PUBLIC_URL}/profile/${profilePicURL}`;
};

/**
 * Access token авах (Basic authentication ашиглан)
 * 3rd party API-уудыг ашиглахын тулд энэ token шаардлагатай
 * Token 2 долоо хоногийн дараа expire болно
 * 
 * @param {string} username - Toki-с өгсөн username
 * @param {string} password - Toki-с өгсөн password
 * @returns {Promise<Object>} - Access token
 */
export const getAccessToken = async (username, password) => {
    try {
        // Create Basic auth token
        const basicToken = btoa(`${username}:${password}`);

        const response = await fetch(`${TOKI_API_URL}/third-party-service/v1/auth/token`, {
            method: 'GET',
            headers: {
                'accept': 'application/json',
                'Authorization': `Basic ${basicToken}`
            }
        });

        const data = await response.json();

        if (data.code === 200) {
            return {
                success: true,
                accessToken: data.data.accessToken
            };
        } else {
            return {
                success: false,
                error: data.error?.message || 'Failed to get access token'
            };
        }
    } catch (error) {
        console.error('Toki getAccessToken error:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Төлбөрийн статус шалгах
 * 
 * @param {string} requestId - Transaction ID / Request ID
 * @param {string} accessToken - Bearer token (getAccessToken-с авсан)
 * @returns {Promise<Object>} - Payment status
 * 
 * Status values:
 * - PENDING: Төлөгдөөгүй
 * - APPROVED: Амжилттай төлөгдсөн (requestId-р шалгахад)
 * - COMPLETED: Амжилттай төлөгдсөн (transactionId-р шалгахад)
 * - EXPIRED: Хугацаа дуусгавар болсон
 */
export const checkPaymentStatus = async (requestId, accessToken) => {
    try {
        const response = await fetch(
            `${TOKI_API_URL}/third-party-service/v1/payment-request/status?requestId=${requestId}`,
            {
                method: 'GET',
                headers: {
                    'accept': '*/*',
                    'api-key': 'third_party_pay',
                    'Authorization': `Bearer ${accessToken}`
                }
            }
        );

        const data = await response.json();

        if (data.code === 200) {
            return {
                success: true,
                status: data.data.status,
                transNumber: data.data.transNumber,
                fee: data.data.fee
            };
        } else {
            return {
                success: false,
                error: data.error?.message || 'Failed to check payment status'
            };
        }
    } catch (error) {
        console.error('Toki checkPaymentStatus error:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Хэрэглэгчид notification илгээх
 * 
 * @param {Object} options - Notification options
 * @param {string} options.title - Notification title (REQUIRED)
 * @param {string} options.body - Notification body (REQUIRED)
 * @param {string} options.buttonName - Button name (REQUIRED)
 * @param {string} options.accountId - User's account ID (REQUIRED)
 * @param {string} options.icon - Icon URL (REQUIRED)
 * @param {string} options.merchantId - Merchant ID (REQUIRED)
 * @param {string} options.imageURL - Image URL (OPTIONAL)
 * @param {string} options.url - Redirect URL (OPTIONAL)
 * @param {string} accessToken - Bearer token
 * @returns {Promise<Object>} - Result
 */
export const sendNotification = async (options, accessToken) => {
    try {
        const { title, body, buttonName, accountId, icon, merchantId, imageURL, url } = options;

        // Validate required fields
        if (!title || !body || !buttonName || !accountId || !icon || !merchantId) {
            return {
                success: false,
                error: 'Missing required fields: title, body, buttonName, accountId, icon, merchantId'
            };
        }

        const payload = {
            title,
            body,
            buttonName,
            accountId,
            icon,
            merchantId
        };

        // Add optional fields
        if (imageURL) payload.imageURL = imageURL;
        if (url) payload.url = url;

        const response = await fetch(`${TOKI_API_URL}/third-party-service/v1/notification/single-push`, {
            method: 'POST',
            headers: {
                'accept': '*/*',
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.code === 200) {
            return {
                success: true
            };
        } else {
            return {
                success: false,
                error: data.error?.message || 'Failed to send notification'
            };
        }
    } catch (error) {
        console.error('Toki sendNotification error:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Payment статусууд
 */
export const PAYMENT_STATUS = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    COMPLETED: 'COMPLETED',
    EXPIRED: 'EXPIRED'
};

/**
 * Default Merchant ID (Test environment)
 */
export const DEFAULT_MERCHANT_ID = process.env.REACT_APP_TOKI_MERCHANT_ID || '633420ef504267b0f2288c7d';

export default {
    getProfilePictureUrl,
    getAccessToken,
    checkPaymentStatus,
    sendNotification,
    PAYMENT_STATUS,
    DEFAULT_MERCHANT_ID
};
